/* eslint-disable no-shadow */
import React, { useState, useRef } from 'react';
import PropTypes, { any } from 'prop-types';
import { useSelector } from 'react-redux';
import { orderBy } from 'lodash';

import Dotdotdot from 'react-dotdotdot';
import Img from '../../components/Img';
import { createTypeByIdDict, updateCategoryTypesName } from '../../utils/formatting';
import { drawCategoryTypeIds, drawScheduleTypeIds } from '../../utils/eventConstants';
import { shouldDisplayDraws } from '../../utils/events';
import useClickOutsideRef from '../../components/hooks/useClickOutsideRef';
import { EventDeskTabKeys } from '../../utils/eventDeskConstants';

import styles from './drawEdit.scss';
import { allowDrawCreation } from '../../utils/draws';
import { getDrawName } from '../../utils/courtSchedular';
import { useStatsig } from '../../components/hooks/useStatsig';

const _ = { orderBy };
const trays = {
    DRAWS: 'draws',
    MANAGE: 'manage',
};

function DrawsSidebarLeftV3(props) {
    const {
        isReadOnly,
        drawListFilter,
        setDrawListFilter,
        resetDrawListFilter,
        showDrawCreateModal,
        handleDrawTabClick,
        handleDrawDeleteClick,
        handleDrawEditClick,
        handleChildDrawClick,
        handleScheduleTabClick,
        handleLocationsTabClick,
        handleOfficialsTabClick,
        handleNotificationsTabClick,
        handleAccountsTabClick,
        handlePrintSpoolerTabClick,
        activeView,
        selectedLocation,
        handleLocationSelect,
        currentDraw,
        parentDraw,
        event,
        handlePlayersTabClick,
        scheduleTypeId,
        handleCourtScheduleTabClick,
        handleDayPlannerTabClick,
        handleCheckInTabClick,
        handleNextAvailableTabClick,
    } = props;
    const { checkGate, featureGates } = useStatsig();
    const eventDeskCheckInEnabled = checkGate(featureGates.EVENT_DESK_CHECKIN);
    const coreUI = useSelector(({ coreUI }) => coreUI);
    const { draws, locations } = useSelector(({ drawEdit }) => {
        const { draws, locations } = drawEdit;
        return {
            draws,
            locations,
        };
    });

    const { drawCategoryTypes = [] } = coreUI.types?.tms?.draw || {};
    const drawCategoryTypesDict = updateCategoryTypesName(createTypeByIdDict(drawCategoryTypes));

    const [isCourtsOpen, setIsCourtsOpen] = useState(false);
    const [openTray, setOpenTray] = useState();
    const sideBarRef = useRef(null);

    useClickOutsideRef(sideBarRef, () => setOpenTray());

    const isScheduleEnabled = checkGate(featureGates.EVENT_DESK_SCHEDULE);
    const isDayPlannerEnabled = checkGate(featureGates.EVENT_DESK_DAY_PLANNER);

    const isOrderOfPlay = scheduleTypeId === drawScheduleTypeIds.ORDER_OF_PLAY;
    const isNextAvailable = scheduleTypeId === drawScheduleTypeIds.NEXT_AVAILABLE;

    const sortedLocations = _.orderBy(locations.data || [], [l => l.name?.toLowerCase()]);

    const onAddDrawClick = (e) => {
        e.stopPropagation();
        showDrawCreateModal();
    };

    const onSectionTitleClick = (draw) => {
        handleDrawTabClick(draw);
    };

    const onDrawDeleteClick = (ev, draw) => {
        ev.preventDefault();
        ev.stopPropagation();

        handleDrawDeleteClick(draw);
    };

    const onDrawEditClick = (ev, draw, parentDraw) => {
        ev.preventDefault();
        ev.stopPropagation();

        setOpenTray();
        handleDrawEditClick(draw, parentDraw);
    };

    const onCourtsClick = () => {
        handleLocationsTabClick();
        setIsCourtsOpen(!isCourtsOpen);
    };

    const handleDrawsTabClick = () => {
        setOpenTray(prevTray => (prevTray === trays.DRAWS ? null : trays.DRAWS)); // to open and close draws on click
    };

    const handleManageTabClick = () => {
        setOpenTray(trays.MANAGE);
    };

    const getTabColor = (tabName) => {
        return activeView === tabName ? 'white' : 'black';
    };

    const isManageTab = activeView === 'scheduleTab' || activeView === 'locationsTab' || activeView === 'officialsTab';
    const isDrawCreationAllowed = allowDrawCreation(event);

    return (
        <div className={styles.sideBar} ref={sideBarRef} data-testid="draw-edit.sidebar">
            <div className={`${styles.buttonWrapper} ${activeView === 'playersTab' ? 'active' : ''}`} onClick={() => handlePlayersTabClick()} data-testid="draw-edit.sidebar.players-tab">
                <div className={styles.button}>
                    <Img className="close-icon" src={`/img/v3/icons/icon-24px-player${activeView === 'playersTab' ? '-white' : ''}.svg`} />
                    <div className={styles.label} style={{ color: getTabColor('playersTab') }} data-testid="draw-edit.sidebar.players-tab.label">
                        Players
                    </div>
                </div>
            </div>
            {isDrawCreationAllowed && (
                <div className={`${styles.buttonWrapper} ${activeView === 'drawEditor' ? 'active' : ''}`} onClick={() => handleDrawsTabClick()} data-testid="draw-edit.sidebar.draws-tab">
                    <div className={styles.button}>
                        <Img className="close-icon" src={`/img/v3/icons/icon-24px-draws${activeView === 'drawEditor' ? '-white' : ''}.svg`} />
                        <div className={styles.label} style={{ color: getTabColor('drawEditor') }} data-testid="draw-edit.sidebar.draws-tab.label">
                            Draws
                        </div>
                    </div>
                </div>
            )}
            {openTray === trays.DRAWS && (
                <div className={styles.tray}>
                    <div className={styles.trayInner}>
                        <div className={styles.trayHeader} data-testid="draw-edit.sidebar.draws-tray.title">
                            Draws {isDrawCreationAllowed && !isReadOnly && <Img id="addDraw" className="close-icon" src="/img/v3/icons/icon-24px-plus.svg" onClick={onAddDrawClick} data-testid="draw-edit.sidebar.draws-tray.add-draw-btn" />}
                        </div>
                        <div className={`${styles.section} ${styles.search}`}>
                            <div className={styles.search}>
                                <input
                                    type="text"
                                    placeholder="Find a draw"
                                    value={drawListFilter}
                                    onChange={setDrawListFilter}
                                    data-testid="draw-edit.sidebar.draw-filter-input"
                                />
                                <i className={`uts uts-close ${drawListFilter ? 'd-block' : 'd-none'}`} onClick={resetDrawListFilter} data-testid="draw-edit.sidebar.draw-filter-clear" />
                            </div>
                        </div>
                        <div className={styles.scrollable}>
                            {(draws.data || []).map((draw, i) => {
                                const filter = drawListFilter.replace(/[-[\]{}()*+?.,\\/^$|#\s]/g, '\\$&');
                                const regex = new RegExp(filter, 'i');
                                const isOpen = currentDraw?.id === draw.id || (parentDraw?.id === draw.id && activeView === 'drawEditor');
                                const isParentDraw = draw.childDraws?.length > 0;
                                const shouldShowArrow = isParentDraw || (!isParentDraw && currentDraw?.id !== draw.id);
                                const shouldNotDisplayDraw = drawListFilter && !draw.name.match(regex);
                                if (!shouldNotDisplayDraw) {
                                    return (
                                        <div className={`${styles.section} ${styles.sectionNew}`} key={draw.id} data-testid={`draw-edit.sidebar.draw-${draw.id}`}>
                                            <div
                                                className={`${styles.sectionTitleNew} ${isOpen ? styles.isOpen : ''} ${currentDraw?.id === draw.id ? styles.selectedParentDraw : ''}`}
                                                onClick={() => onSectionTitleClick(draw, i)}
                                                data-testid={`draw-edit.sidebar.draw-${draw.id}.title`}
                                            >
                                                <div className={styles.iconWrapper}>
                                                    {shouldShowArrow && (
                                                        <i className={`fas fa-solid fa-caret-${isOpen ? 'down' : 'right'}`} />
                                                    )}
                                                </div>
                                                <div className={`${styles.drawName} flex-1`} data-testid={`draw-edit.sidebar.draw-${draw.id}.name`}>
                                                    <Dotdotdot clamp={2}>{draw.name}{parentDraw?.id === draw.id && draw.childDraws?.length && draw.categoryTypeId !== drawCategoryTypeIds.GROUPED ? ` - ${getDrawName(draw, draw, drawCategoryTypesDict)}` : ''}</Dotdotdot>
                                                </div>
                                                {draw.canEditDraw && (
                                                    <button
                                                        className={`btn ${styles.drawAction}`}
                                                        type="button"
                                                        onClick={(e) => onDrawEditClick(e, draw)}
                                                        data-testid={`draw-edit.sidebar.draw-${draw.id}.edit-btn`}
                                                    >
                                                        <Img src="/img/v3/icons/icon-16px-edit-blue.svg" />
                                                    </button>
                                                )}
                                                {draw.canDeleteDraw && (
                                                    <button
                                                        className={`btn ${styles.drawAction} p0 mr4`}
                                                        type="button"
                                                        onClick={(e) => onDrawDeleteClick(e, draw.id)}
                                                        data-testid={`draw-edit.sidebar.draw-${draw.id}.delete-btn`}
                                                    >
                                                        <Img src="/img/v3/icons/icon-16px-delete-blue.svg" />
                                                    </button>
                                                )}
                                            </div>
                                            {isOpen && (
                                                <>
                                                    {draw.childDraws?.filter(shouldDisplayDraws).map((childDraw) => (
                                                        <div
                                                            key={childDraw.id}
                                                            className={`${styles.childDrawNew} ${currentDraw?.id === childDraw.id && activeView === 'drawEditor' ? 'active' : ''} ${currentDraw?.id === childDraw.id ? styles.selectedChildDraw : ''}`}
                                                            onClick={(e) => handleChildDrawClick(e, childDraw.id)}
                                                            data-testid={`draw-edit.sidebar.draw-${draw.id}.child-${childDraw.id}`}
                                                        >
                                                            <div className="flex-1" data-testid={`draw-edit.sidebar.draw-${draw.id}.child-${childDraw.id}.name`}>
                                                                {getDrawName(childDraw, draw, drawCategoryTypesDict)}
                                                            </div>
                                                            {childDraw.canEditDraw && (
                                                                <button
                                                                    className={`btn ${styles.drawAction}`}
                                                                    type="button"
                                                                    onClick={(e) => onDrawEditClick(e, childDraw, draw)}
                                                                    data-testid={`draw-edit.sidebar.draw-${draw.id}.child-${childDraw.id}.edit-btn`}
                                                                >
                                                                    <Img src="/img/v3/icons/icon-16px-edit-blue.svg" />
                                                                </button>
                                                            )}
                                                            {childDraw.canDeleteDraw && (
                                                                <button
                                                                    className={`btn ${styles.drawAction}`}
                                                                    type="button"
                                                                    onClick={(e) => onDrawDeleteClick(e, childDraw.id)}
                                                                    data-testid={`draw-edit.sidebar.draw-${draw.id}.child-${childDraw.id}.delete-btn`}
                                                                >
                                                                    <Img src="/img/v3/icons/icon-16px-delete-blue.svg" />
                                                                </button>
                                                            )}
                                                        </div>
                                                    ))}
                                                </>
                                            )}
                                        </div>
                                    );
                                }

                                return null;
                            })}
                        </div>
                    </div>
                </div>
            )}
            {isOrderOfPlay && isScheduleEnabled && (
                <div
                    className={`${styles.buttonWrapper} ${activeView === 'courtSchedulerTab' ? 'active' : ''}`}
                    onClick={handleCourtScheduleTabClick}
                    data-testid="draw-edit.sidebar.court-schedule-tab"
                >
                    <div className={styles.button}>
                        <Img
                            className="close-icon"
                            src={`/img/v3/icons/icon-24px-calendar${activeView === 'courtSchedulerTab' ? '-white' : ''}.svg`}
                        />
                        <div className={styles.label} style={{ color: getTabColor('courtSchedulerTab') }} data-testid="draw-edit.sidebar.court-schedule-tab.label">
                            Schedule
                        </div>
                    </div>
                </div>
            )}
            {(isOrderOfPlay || isNextAvailable) && isDayPlannerEnabled && (
                <div
                    className={`${styles.buttonWrapper} ${activeView === 'dayPlanner' ? 'active' : ''}`}
                    onClick={handleDayPlannerTabClick}
                    data-testid="draw-edit.sidebar.day-planner-tab"
                >
                    <div className={styles.button}>
                        <Img
                            className="close-icon"
                            src={`/img/v3/icons/icon-24px-calendar${activeView === 'dayPlanner' ? '-white' : ''}.svg`}
                        />
                        <div className={styles.label} style={{ color: getTabColor('dayPlanner') }} data-testid="draw-edit.sidebar.day-planner-tab.label">
                            Planner
                        </div>
                    </div>
                </div>
            )}
            {isNextAvailable && (
                <div
                    className={`${styles.buttonWrapper} ${activeView === 'nextAvailable' ? 'active' : ''}`}
                    onClick={handleNextAvailableTabClick}
                    data-testid="draw-edit.sidebar.next-available-tab"
                >
                    <div className={styles.button}>
                        <Img
                            className="close-icon"
                            src={`/img/v3/icons/icon-24px-next-available${activeView === 'nextAvailable' ? '-white' : ''}.svg`}
                        />
                        <div className={styles.label} style={{ color: getTabColor('nextAvailable') }} data-testid="draw-edit.sidebar.next-available-tab.label">
                            Next avail
                        </div>
                    </div>
                </div>
            )}
            {eventDeskCheckInEnabled && (
                <div className={`${styles.buttonWrapper} ${activeView === 'checkInTab' ? 'active' : ''}`} onClick={() => handleCheckInTabClick()} data-testid="draw-edit.sidebar.check-in-tab">
                    <div className={styles.button}>
                        <Img className="close-icon" src={`/img/v3/icons/icon-check-in-24px${activeView === 'checkInTab' ? '-white' : ''}.svg`} />
                        <div className={styles.label} style={{ color: getTabColor('checkInTab') }} data-testid="draw-edit.sidebar.check-in-tab.label">
                            Check in
                        </div>
                    </div>
                </div>
            )}
            <div className={`${styles.buttonWrapper} ${isManageTab ? 'active' : ''}`} onClick={() => handleManageTabClick()} data-testid="draw-edit.sidebar.manage-tab">
                <div className={styles.button}>
                    <Img className="close-icon" src={`/img/v3/icons/icon-24px-settings${isManageTab ? '-white' : ''}.svg`} />
                    <div className={styles.label} style={{ color: isManageTab ? 'white' : 'black' }} data-testid="draw-edit.sidebar.manage-tab.label">
                        Manage
                    </div>
                </div>
            </div>
            {openTray === trays.MANAGE && (
                <div className={styles.tray}>
                    <div className={styles.trayHeader} data-testid="draw-edit.sidebar.manage-tray.title">
                        Manage
                    </div>
                    <div
                        className={styles.section}
                        onClick={() => handleScheduleTabClick()}
                    >
                        <div className={`${styles.sectionTitle} ${activeView === 'scheduleTab' ? styles.isOpen : ''}`} data-testid="draw-edit.sidebar.manage-schedule-type">
                            Schedule type
                        </div>
                    </div>
                    <div className={styles.section}>
                        <div
                            className={`${styles.sectionTitle} ${isCourtsOpen ? styles.isOpen : ''}`}
                            onClick={onCourtsClick}
                            data-testid="draw-edit.sidebar.manage-courts"
                        >
                            <div className={styles.iconWrapper}>
                                <i className={`fas fa-solid fa-caret-${isCourtsOpen ? 'down' : 'right'}`} />
                            </div>
                            <div className={styles.drawName} data-testid="draw-edit.sidebar.manage-courts.label">
                                Courts / facilities
                            </div>
                        </div>
                        {isCourtsOpen && sortedLocations.map((location) => (
                            <div
                                key={location.id}
                                className={`${styles.childDraw} ${selectedLocation?.id === location.id ? 'active' : ''}`}
                                onClick={(e) => handleLocationSelect(e, location.id)}
                                data-testid={`draw-edit.sidebar.manage-courts.location-${location.id}`}
                            >
                                {location.name}
                            </div>
                        ))}
                    </div>
                    <div className={styles.section}>
                        <div
                            className={`${styles.sectionTitle} ${activeView === 'officialsTab' ? styles.isOpen : ''}`}
                            onClick={handleOfficialsTabClick}
                            data-testid="draw-edit.sidebar.manage-officials"
                        >
                            Officials
                        </div>
                    </div>
                    {(event?.enableEventDeskNotifications && (
                        <div className={styles.section}>
                            <div
                                className={`${styles.sectionTitle} ${activeView === EventDeskTabKeys.NOTIFICATIONS ? styles.isOpen : ''}`}
                                onClick={handleNotificationsTabClick}
                                data-testid="draw-edit.sidebar.manage-notifications"
                            >
                                Notifications
                            </div>
                        </div>
                    ))}
                    {eventDeskCheckInEnabled && (
                        <div className={styles.section}>
                            <div
                                className={`${styles.sectionTitle} ${activeView === EventDeskTabKeys.ACCOUNTS ? styles.isOpen : ''}`}
                                onClick={handleAccountsTabClick}
                                data-testid="draw-edit.sidebar.manage-accounts"
                            >
                                Accounts
                            </div>
                        </div>
                    )}
                    {isNextAvailable && (
                        <div className={styles.section}>
                            <div
                                className={`${styles.sectionTitle} ${activeView === EventDeskTabKeys.PRINT_SPOOLER ? styles.isOpen : ''}`}
                                onClick={handlePrintSpoolerTabClick}
                                data-testid="draw-edit.sidebar.manage-print-spooler"
                            >
                                Print Spooler
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

DrawsSidebarLeftV3.propTypes = {
    isReadOnly: PropTypes.bool,
    drawListFilter: PropTypes.string,
    setDrawListFilter: PropTypes.func.isRequired,
    resetDrawListFilter: PropTypes.func.isRequired,
    showDrawCreateModal: PropTypes.func.isRequired,
    handleDrawTabClick: PropTypes.func.isRequired,
    handleDrawDeleteClick: PropTypes.func.isRequired,
    handleDrawEditClick: PropTypes.func.isRequired,
    handleChildDrawClick: PropTypes.func.isRequired,
    handleScheduleTabClick: PropTypes.func.isRequired,
    handleLocationsTabClick: PropTypes.func.isRequired,
    handleOfficialsTabClick: PropTypes.func.isRequired,
    handleAccountsTabClick: PropTypes.func,
    activeView: PropTypes.string.isRequired,
    selectedLocation: PropTypes.objectOf(any),
    handleLocationSelect: PropTypes.func.isRequired,
    currentDraw: PropTypes.objectOf(any),
    parentDraw: PropTypes.objectOf(any),
    event: PropTypes.objectOf(any).isRequired,
    handlePlayersTabClick: PropTypes.func.isRequired,
    handleCheckInTabClick: PropTypes.func.isRequired,
    scheduleTypeId: PropTypes.number.isRequired,
    handleCourtScheduleTabClick: PropTypes.func.isRequired,
    handleDayPlannerTabClick: PropTypes.func.isRequired,
    handleNextAvailableTabClick: PropTypes.func.isRequired,
    handlePrintSpoolerTabClick: PropTypes.func.isRequired,
};

DrawsSidebarLeftV3.defaultProps = {
    isReadOnly: false,
    drawListFilter: '',
    selectedLocation: {},
    handleAccountsTabClick: () => { },
    currentDraw: null,
    parentDraw: null,
};

export default DrawsSidebarLeftV3;
